import React, { useState, useEffect } from 'react';
import { X, Plus, AlertCircle } from 'lucide-react';
import { IntegrationField } from '../../types';
import SoarFieldMappingForm from './SoarFieldMappingForm';

interface IntegrationFieldFormProps {
  initialField?: IntegrationField;
  onSubmit: (field: IntegrationField) => void;
  onCancel: () => void;
  existingFieldKeys: string[];
  predefinedType?: 'text' | 'number' | 'secret' | 'url' | 'multi-select' | 'soar-mapping';
  isServicesField?: boolean;
}

const IntegrationFieldForm: React.FC<IntegrationFieldFormProps> = ({
  initialField,
  onSubmit,
  onCancel,
  existingFieldKeys = [],
  predefinedType,
  isServicesField = false
}) => {
  // Create a deep copy of the initial field to avoid reference issues
  const getInitialField = () => {
    // Start with default values
    const defaultField: IntegrationField = {
      field_key: '',
      label: '',
      type: 'text',
      is_required: false,
      options: [],
      field_mapping: {}
    };
    
    // If we have an initial field, deep clone it
    if (initialField) {
      const clonedField = JSON.parse(JSON.stringify(initialField));
      
      // Ensure options array exists for multi-select fields
      if (clonedField.type === 'multi-select' && !Array.isArray(clonedField.options)) {
        clonedField.options = [];
      }
      
      return clonedField;
    }
    
    // Special handling for services field
    if (isServicesField) {
      return {
        field_key: 'services',
        label: 'Services',
        type: 'multi-select',
        is_required: false,
        options: []
      };
    }
    
    // Special handling for SOAR mapping field
    if (predefinedType === 'soar-mapping') {
      return {
        field_key: 'soar_field_mapping',
        label: 'Soar Field Mapping',
        type: 'soar-mapping',
        is_required: false,
        field_mapping: {}
      };
    }
    
    // Apply predefined type if specified
    if (predefinedType) {
      defaultField.type = predefinedType;
    }
    
    return defaultField;
  };
  
  // Initialize state with the processed initial field
  const [field, setField] = useState<IntegrationField>(getInitialField);
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [option, setOption] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Field state tracking for multi-select fields
  useEffect(() => {
    // Track field state changes for multi-select fields
  }, [field]);
  
  // Handle input change for text fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setField(prev => ({ ...prev, [name]: checked }));
    } else {
      setField(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Add an option to the multi-select field
  const addOption = (e?: React.MouseEvent) => {
    // Prevent form submission if called from button click
    if (e) {
      e.preventDefault();
    }
    
    if (!option.trim()) return;
    
    // Create a new options array with the new option
    const newOptions = [...(field.options || []), option.trim()];
    
    // Update the field state with the new options array
    setField(prev => ({
      ...prev,
      options: newOptions
    }));
    
    // Options updated successfully
    
    // Reset option input
    setOption('');
  };
  
  // Remove an option from the multi-select field
  const removeOption = (optionToRemove: string, e?: React.MouseEvent) => {
    // Prevent form submission if called from button click
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Create a new options array without the removed option
    const newOptions = (field.options || []).filter(opt => opt !== optionToRemove);
    
    // Update the field state with the new options array
    setField(prev => ({
      ...prev,
      options: newOptions
    }));
    
    // Options updated successfully
  };
  
  // Handle soar field mapping changes
  const handleFieldMappingChange = (mapping: Record<string, string>) => {
    // Create a deep copy of the mapping to prevent reference issues
    const mappingCopy = JSON.parse(JSON.stringify(mapping));
    setField(prev => ({
      ...prev,
      field_mapping: mappingCopy
    }));
  };
  
  // Validate the form before submission
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    // For SOAR mapping, we don't validate field_key and label since they're automatically set
    if (field.type !== 'soar-mapping') {
      // Validate field key
      if (!field.field_key.trim()) {
        newErrors.field_key = 'Field key is required';
      } else if (!initialField && existingFieldKeys.includes(field.field_key)) {
        newErrors.field_key = 'Field key already exists';
      }
      
      // Validate label
      if (!field.label.trim()) {
        newErrors.label = 'Label is required';
      }
    }
    
    // Validate multi-select options - no longer required
    if (field.type === 'multi-select' && (!field.options || field.options.length === 0)) {
      // For services field, warn but don't block submission
      if (field.field_key === 'services') {
        console.warn("Services field has no options");
      }
      // No validation error - options are optional now
    }
    
    // Validate soar mapping fields
    if (field.type === 'soar-mapping' && 
        (!field.field_mapping || Object.keys(field.field_mapping).length === 0)) {
      newErrors.field_mapping = 'At least one field mapping is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle field submission
  const handleSubmit = (e?: React.MouseEvent) => {
    // Prevent default form submission which causes page refresh
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (isSubmitting) return;
    
    // For SOAR mapping, automatically set field_key and label
    if (field.type === 'soar-mapping') {
      field.field_key = 'soar_field_mapping';
      field.label = 'SOAR Field Mapping';
    }
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        // Create a deep copy of the field for submission
        const fieldToSubmit = JSON.parse(JSON.stringify(field));
        
        // Ensure options array is properly formatted for multi-select fields
        if (fieldToSubmit.type === 'multi-select') {
          // Ensure options is an array
          fieldToSubmit.options = Array.isArray(fieldToSubmit.options) 
            ? fieldToSubmit.options 
            : [];
          
          // Services field processing
          if (fieldToSubmit.field_key === 'services') {
            // Process services field options
          }
        }
        
        // Ensure field_mapping is properly formatted for soar-mapping fields
        if (fieldToSubmit.type === 'soar-mapping') {
          // Ensure field_mapping is an object
          fieldToSubmit.field_mapping = fieldToSubmit.field_mapping || {};
          
          // Process SOAR mapping field
        }
        
        // Submit the field to the parent component
        
        // Submit the field to the parent component
        onSubmit(fieldToSubmit);
      } catch (error) {
        console.error("Error preparing field for submission:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  // Handle Enter key in option input to add option instead of submitting form
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addOption();
    }
  };
  
  return (
    <div className="bg-background-hover border border-border-dark rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">
          {field.type === 'soar-mapping' ? 'SOAR Field Mapping Configuration' : 
           initialField ? 'Edit Field' : isServicesField ? 'Add Services Field' : 'Add Field'}
        </h3>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }}
          className="text-text-muted-dark hover:text-text-light"
        >
          <X size={16} />
        </button>
      </div>
      
      <div className="field-form">
        {/* Hide the field inputs when working with SOAR mapping */}
        {field.type !== 'soar-mapping' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Field Key */}
            <div>
              <label htmlFor="field_key" className="block text-sm font-medium text-text-muted-dark mb-1">
                Field Key <span className="text-status-danger">*</span>
              </label>
              <input
                id="field_key"
                name="field_key"
                type="text"
                className={`form-input ${errors.field_key ? 'border-status-danger' : ''}`}
                placeholder="e.g., api_key"
                value={field.field_key}
                onChange={handleInputChange}
                disabled={isServicesField || !!initialField || field.type === 'soar-mapping'}
              />
              {errors.field_key && (
                <p className="text-status-danger text-xs mt-1">{errors.field_key}</p>
              )}
            </div>
            
            {/* Label */}
            <div>
              <label htmlFor="label" className="block text-sm font-medium text-text-muted-dark mb-1">
                Label <span className="text-status-danger">*</span>
              </label>
              <input
                id="label"
                name="label"
                type="text"
                className={`form-input ${errors.label ? 'border-status-danger' : ''}`}
                placeholder="e.g., API Key"
                value={field.label}
                onChange={handleInputChange}
                disabled={isServicesField || field.type === 'soar-mapping'}
              />
              {errors.label && (
                <p className="text-status-danger text-xs mt-1">{errors.label}</p>
              )}
            </div>
            
            {/* Field Type */}
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-text-muted-dark mb-1">
                Field Type <span className="text-status-danger">*</span>
              </label>
              <select
                id="type"
                name="type"
                className="form-select"
                value={field.type}
                onChange={handleInputChange}
                disabled={isServicesField || !!predefinedType}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="secret">Secret</option>
                <option value="url">URL</option>
                <option value="multi-select">Multi-Select</option>
                <option value="soar-mapping">Soar Field Mapping</option>
              </select>
            </div>
            
            {/* Is Required - not shown for soar-mapping type */}
            {field.type !== 'soar-mapping' && (
              <div className="flex items-center pt-6">
                <input
                  id="is_required"
                  name="is_required"
                  type="checkbox"
                  className="form-checkbox h-4 w-4"
                  checked={field.is_required}
                  onChange={handleInputChange}
                />
                <label htmlFor="is_required" className="ml-2 block text-sm font-medium text-text-muted-dark">
                  Required Field
                </label>
              </div>
            )}
          </div>
        )}
        
        {/* Default Value - only shown for non-multi-select and non-soar-mapping fields */}
        {field.type !== 'multi-select' && field.type !== 'soar-mapping' && (
          <div className="mb-4">
            <label htmlFor="default_value" className="block text-sm font-medium text-text-muted-dark mb-1">
              Default Value
            </label>
            <input
              id="default_value"
              name="default_value"
              type="text"
              className="form-input"
              placeholder="Default value (optional)"
              value={field.default_value || ''}
              onChange={handleInputChange}
            />
          </div>
        )}
        
        {/* Options for multi-select fields */}
        {field.type === 'multi-select' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-muted-dark mb-1">
              Options
            </label>
            
            <div className="flex flex-wrap gap-2 mb-2 min-h-[32px]">
              {field.options && field.options.length > 0 ? (
                field.options.map((opt, index) => (
                  <div key={index} className="text-text-light px-2 py-1 rounded-md flex items-center text-sm border border-gray-700/40">
                    <span>{opt}</span>
                    <button 
                      type="button" 
                      onClick={(e) => removeOption(opt, e)} 
                      className="ml-1 text-text-muted-dark hover:text-text-light"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-text-muted-dark text-sm">
                  No options added yet (optional)
                </div>
              )}
            </div>
            
            {errors.options && (
              <div className="mb-2 flex items-center text-status-danger text-xs">
                <AlertCircle size={12} className="mr-1" /> {errors.options}
              </div>
            )}
            
            <div className="flex items-center">
              <input
                type="text"
                className="form-input mr-2"
                placeholder="Add option..."
                value={option}
                onChange={(e) => setOption(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button
                type="button"
                onClick={addOption}
                className="btn-add px-3 py-1.5"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        )}
        
        {/* Field Mapping for soar-mapping type */}
        {field.type === 'soar-mapping' && (
          <div className="mb-4">
            <SoarFieldMappingForm 
              initialMapping={field.field_mapping || {}}
              onChange={handleFieldMappingChange}
            />
            
            {errors.field_mapping && (
              <div className="mt-2 flex items-center text-status-danger text-xs">
                <AlertCircle size={12} className="mr-1" /> {errors.field_mapping}
              </div>
            )}
          </div>
        )}
        
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn-subdued-secondary px-4 py-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="btn-subdued-primary px-4 py-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : initialField ? 'Update Field' : 'Add Field'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationFieldForm; 